import React, { useEffect, useState } from "react";
import { Alert, Autocomplete, Button, Container, FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Tooltip } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from 'dayjs';
import { NavLink } from "react-router-dom";
import { Icons } from "../../icons";
import Json from "../../json";

import { ISelectOption } from "../../../types/list";
import { IShipmentSearchValueSets } from "../../../types/shipment";
import { IShipment_DeletedSearchRequest, IShipment_DeletedSearchResult } from "../../../types/deleted";

import { AppConfig } from "../../../config/appConfig";
import { ErrorProvider } from "../../../classes/errorProvider";
import { useUserContext } from "../../../contexts/userContext";

import { DeletedService } from "../../../services/deletedService";
import { ShipmentService } from "../../../services/shipmentService";

import DeletedShipmentTable from "../../tables/deleted/deletedShipmentTable";
import Navigation from "../../../components/header";
import PermissionGate from "../../permissionGate";
import WorkingModal, { IWorkingModalProps } from "../../workingModal";

import "../../../styles/deleted/deletedShipmentList.scss";

const DeletedShipmentList = () => {
	const userContext = useUserContext();
	const [searchResults, setSearchResults] = useState<IShipment_DeletedSearchResult>();
	const [includedVendorIds, setIncludedVendorIds] = useState<number[]>([]);
	const [includedCustomerIds, setIncludedCustomerIds] = useState<number[]>([]);

	//Date range
	const today = new Date();
	const [startDate, setStartDate] = useState(new Date(today.getFullYear(), today.getMonth() - 1, 1));
	const [endDate, setEndDate] = useState(new Date(today.getFullYear(), today.getMonth(), today.getDate()));

	//Filtering
	const [customerOptions, setCustomerOptions] = useState<ISelectOption[]>([]);
	const [vendorOptions, setVendorOptions] = useState<ISelectOption[]>([]);
	const [statusOptions, setStatusOptions] = useState<string[]>(["Reconciling", "Escalated", "Reviewing", "Auditing", "QB Entry", "QB Audit", "Complete"]);
	const [bolFilter, setBolFilter] = useState('');
	const [manifestNumberFilter, setManifestNumberFilter] = useState('');
	const [invoiceFilter, setInvoiceFilter] = useState('');
	const [customerFilter, setCustomerFilter] = useState('');
	const [vendorFilter, setVendorFilter] = useState('');
	const [statusFilter, setStatusFilter] = useState('');
	const [sortField, setSortField] = useState(AppConfig.deletedShipmentListDefaultSortField.replace(/^-/, ''));
	const [sortAscending, setSortAscending] = useState(!AppConfig.deletedShipmentListDefaultSortField.startsWith('-'));

	const [isLoadingValueSets, setIsLoadingValueSets] = useState<boolean>(false);
	const [isLoadingShipments, setIsLoadingShipments] = useState<boolean>(false);
	const [searchId, setSearchId] = useState<{ value: string }>({ value: '0' });
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [successMessage, setSuccessMessage] = useState<string | React.ReactNode | undefined>();
	const [restoringErrorMessage, setRestoringErrorMessage] = useState<string | undefined>();
	const [criteriaErrorMessage, setCriteriaErrorMessage] = useState<string | undefined>();
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);
	const [isRestoringShipment, setIsRestoringShipment] = useState(false);

	const [userHasSearched, setUserHasSearched] = useState(false);
	const [lastSearchRequest, setLastSearchRequest] = useState<IShipment_DeletedSearchRequest>();
	const searchTimeoutMaxAttempts = 5;
	const forceSearchKeys = ['Enter', 'NumpadEnter'];
	const documentEnteredTooltipText = "Not available when searching by BOL, Invoice, or Manifest #.";

	const [customerFilterTypedText, setCustomerFilterTypedText] = useState('');
	const [vendorFilterTypedText, setVendorFilterTypedText] = useState('');

	useEffect(() => {
		loadValueSets();
	}, [])

	useEffect(() => {
		if (userHasSearched) {
			loadShipmentData();
		}
	}, [customerFilter, vendorFilter, statusFilter])

	const loadShipmentData = async (fromUser: boolean = false) => {
		setErrorMessage(undefined);
		setCriteriaErrorMessage(undefined);
		if (!searchCriteriaIsValid()) return;
		setUserHasSearched(userHasSearched || fromUser);

		try {
			const initialSearchRequest: IShipment_DeletedSearchRequest = {
				pageIndex: 0,
				pageSize: AppConfig.defaultPageSize,
				bol: bolFilter,
				customer: customerFilter,
				vendor: vendorFilter,
				status: statusFilter,
				sortField: sortField,
				sortAscending: sortAscending
			}
			await searchShipments(initialSearchRequest);
		}
		catch (error) {
			console.log('DeletedService.searchDeletedShipments: ERROR', error);
			if (error?.code == "ERR_NETWORK") {
				setErrorMessage(AppConfig.apiConnectionErrorMessage)
			}
		}
	}

	const handleStartDateChange = (value: Dayjs | null) => {
		if (value) {
			setStartDate(value.toDate());
		}
	}

	const handleEndDateChange = (value: Dayjs | null) => {
		if (value) {
			setEndDate(value.toDate());
		}
	}

	const handleTextboxKeydown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		// Force a search when certian keys are pressed, like Enter
		if (forceSearchKeys.includes(event.code)) {
			loadShipmentData(true);
		}
	};

	const loadValueSets = async () => {
		setIsLoadingValueSets(true);
		ShipmentService.getSearchValueSets()
			.then((response) => {
				console.log('ShipmentService.getSearchValueSets: SUCCESS', response);
				var valueSets = response.data as IShipmentSearchValueSets;

				setVendorOptions(valueSets.vendors);
				setCustomerOptions(valueSets.customers);
			})
			.catch((error) => {
				console.log('ShipmentService.getSearchValueSets: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
			})
			.finally(() => {
				setIsLoadingValueSets(false);
			});
	};

	const isDocumentNumberEntered = (): boolean => {
		return !!bolFilter || !!invoiceFilter || !!manifestNumberFilter;
	}

	const blurActiveControl = () => {
		// A timeout of 0ms moves the blur execution to the end of the event loop,
		// ensuring it runs after MUI finishes its internal focus logic.
		setTimeout(() => {
			if (document.activeElement instanceof HTMLElement) {
				document.activeElement.blur();
			}
		}, 0);
	};

	const searchCriteriaIsValid = (): boolean => {
		if (isNaN(startDate.valueOf())) {
			setCriteriaErrorMessage("Start date is invalid.");
			return false;
		}
		if (isNaN(endDate.valueOf())) {
			setCriteriaErrorMessage("End date is invalid.");
			return false;
		}
		return true;
	}

	const searchShipments = async (searchRequest: IShipment_DeletedSearchRequest) => {
		setErrorMessage(undefined);
		setIsLoadingShipments(true);
		setSearchResults(undefined);
		const newSearchId = crypto.randomUUID();
		searchId.value = newSearchId; // Directly modifying a state property like this is gross but it's the only way to be able evaluate the change immediately in the promise resolver
		searchRequest.startDate = startDate;
		searchRequest.endDate = endDate;
		searchRequest.bol = bolFilter;
		searchRequest.invoice = invoiceFilter;
		searchRequest.manifestNumber = manifestNumberFilter;
		searchRequest.customer = customerFilter;
		searchRequest.status = statusFilter;
		searchRequest.vendor = vendorFilter;
		searchRequest.searchId = newSearchId;
		setLastSearchRequest(searchRequest);
		DeletedService.searchDeletedShipments(searchRequest)
			.then((response) => searchDeletedSuccessful(response))
			.catch((error) => {
				console.log('ShipmentService.searchShipments: ERROR', error);

				const properties = ErrorProvider.getErrorProperties(error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
				else if (properties.isSqlTimeout) {
					if (searchId.value !== newSearchId) {
						console.log('A new search has been initiated since this request was made. Aborting search.', { currentSearchId: newSearchId, activeSearchId: searchId.value });
						return;
					}

					retrySearchShipments(1);
					return;
				}
				setErrorMessage(ErrorProvider.getError(error));
				setIncludedCustomerIds([]);
				setIncludedVendorIds([]);
				setIsLoadingShipments(false);
			});
	};

	const searchDeletedSuccessful = (response: any) => {
		console.log('DeletedService.searchDeletedShipments: SUCCESS', response);
		const results = response.data as IShipment_DeletedSearchResult;

		// If another search was started before this one returned, ignore this one's results
		if (results.searchId !== searchId.value) return;

		results.records = DeletedService.mapShipments(results.records);
		setSearchResults(results);
		setIncludedVendorIds(results.includedVendors);
		setIncludedCustomerIds(results.includedCustomers);
		setIsLoadingShipments(false);
	}

	const searchFailure = (error: any, attemptNumber: number, currentSearchId: string) => {
		console.log(`DeletedService.searchDeletedShipments (Attempt ${attemptNumber} of ${searchTimeoutMaxAttempts}): ERROR`, error);
		if (searchId.value !== currentSearchId) {
			console.log('A new search has been initiated since this request was made. Aborting search.', { currentSearchId, activeSearchId: searchId.value });
			return;
		}

		const properties = ErrorProvider.getErrorProperties(error);
		if (error?.code == "ERR_NETWORK") {
			setErrorMessage(AppConfig.apiConnectionErrorMessage)
		}
		else if (properties.isSqlTimeout) {
			if (attemptNumber <= searchTimeoutMaxAttempts) {
				retrySearchShipments(attemptNumber + 1);
				return;
			}
			else {
				setErrorMessage("The search request timed out after multiple attempts. Please try again later.");
			}
		}
		setErrorMessage(ErrorProvider.getError(error));
		setIncludedCustomerIds([]);
		setIncludedVendorIds([]);
		setIsLoadingShipments(false);
	}

	const retrySearchShipments = (attemptNumber: number) => {
		console.log(`Search request timed out. Retrying (Attempt ${attemptNumber} of ${searchTimeoutMaxAttempts})`);

		const newSearchId = crypto.randomUUID();
		searchId.value = newSearchId;
		var request = { ...lastSearchRequest, searchId: newSearchId } as IShipment_DeletedSearchRequest;
		setLastSearchRequest(request);
		DeletedService.searchDeletedShipments(request)
			.then((response) => searchDeletedSuccessful(response))
			.catch((error) => searchFailure(error, attemptNumber, newSearchId));
	}

	const criteriaUpdated = async (searchRequest: IShipment_DeletedSearchRequest) => {
		setSortField(searchRequest.sortField || AppConfig.deletedShipmentListDefaultSortField.replace(/^-/, ''));
		setSortAscending(searchRequest.sortAscending ?? !AppConfig.deletedShipmentListDefaultSortField.startsWith('-'));
		await searchShipments(searchRequest);
	};

	const handleRestoreShipmentRequest = (shipmentId: number, bol?: string) => {
		setRestoringErrorMessage(undefined);
		setIsRestoringShipment(true);
		setWorkingModalParams({ params: { content: `Restoring shipment${bol ? ` "${bol}"` : ""}...` } });

		DeletedService.softRestoreDeletedShipment(shipmentId)
			.then((response) => {
				console.log('DeletedService.softRestoreDeletedShipment: SUCCESS', response);
				setSearchResults(prev => ({
					...prev,
					records: prev.records.filter(record => record.id !== shipmentId)
				}));
				setSuccessMessage(<>Shipment {bol ? `"${bol}"` : shipmentId} has been successfully restored. <NavLink className="open-restored" to={"/shipment/" + shipmentId}><Icons.ViewItem fill="#1e4620" height="14px"></Icons.ViewItem><span>View</span></NavLink></>);
			})
			.catch((error) => {				
				console.log('DeletedService.softRestoreDeletedShipment: ERROR', error);
				setRestoringErrorMessage(ErrorProvider.getError(error))
			})
			.finally(() => {
				setIsRestoringShipment(false);
				setWorkingModalParams(undefined);
			});
	}

	return (
		<div className="hwi-deleted-shipment-list">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canManageShipments}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<section className="hwi-entity-search-bar">
							<div className="search-header">
								<div className="title">Search Deleted Shipments</div>
								<div className="controls">
									<Tooltip title={`${isDocumentNumberEntered() ? documentEnteredTooltipText : ""}`} arrow>
										<div className="date-field">
											<DatePicker
												className="start-date-field"
												label="Shipped on or after"
												disabled={!!bolFilter || !!invoiceFilter || !!manifestNumberFilter}
												format={(!!bolFilter || !!invoiceFilter || !!manifestNumberFilter) ? "[N/A]" : undefined}
												value={dayjs(startDate)}
												onChange={(value) => handleStartDateChange(value)} />
										</div>
									</Tooltip>
									<Tooltip title={`${isDocumentNumberEntered() ? documentEnteredTooltipText : ""}`} arrow>
										<div className="date-field">
											<DatePicker
												className="end-date-field"
												label="Shipped on or before"
												disabled={!!bolFilter || !!invoiceFilter || !!manifestNumberFilter}
												format={(!!bolFilter || !!invoiceFilter || !!manifestNumberFilter) ? "[N/A]" : undefined}
												value={dayjs(endDate)}
												onChange={(value) => handleEndDateChange(value)} />
										</div>
									</Tooltip>
								</div>
							</div>
							<div className="search-options">
								<FormControl size="small" className="bol-field">
									<TextField
										label="BOL #"
										name="bol"
										value={bolFilter}
										size="small"
										onKeyDown={(event) => handleTextboxKeydown(event)}
										onChange={(event) => { setBolFilter(event.target.value); }}>
									</TextField>
								</FormControl>
								<FormControl size="small" className="invoice-field">
									<TextField
										label="Invoice #"
										name="invoice"
										value={invoiceFilter}
										size="small"
										onKeyDown={(event) => handleTextboxKeydown(event)}
										onChange={(event) => { setInvoiceFilter(event.target.value); }}>
									</TextField>
								</FormControl>
								<FormControl size="small" className="manifest-number-field">
									<TextField
										label="Manifest #"
										name="manifest"
										value={manifestNumberFilter}
										size="small"
										onKeyDown={(event) => handleTextboxKeydown(event)}
										onChange={(event) => { setManifestNumberFilter(event.target.value); }}>
									</TextField>
								</FormControl>
								<FormControl size="small" className={`customer-field ${isLoadingShipments && "disabled-appearance"}`}>
									<Autocomplete
										autoHighlight
										blurOnSelect
										disablePortal
										disableClearable
										options={[{ name: 'All', value: '' }, ...customerOptions.filter(c => !searchResults?.records?.length || (includedCustomerIds?.includes(+c.value) ?? false))]}
										renderInput={(params) => <TextField {...params} label="Customer" />}
										renderOption={(props, item: ISelectOption, { index }) => (<li {...props} className={!item.isActive && item.value ? "filter-item-inactive" : ""} value={item.value} key={index}>{item.name}{!item.isActive && item.value && " (Inactive)"}</li>)}
										inputValue={customerFilterTypedText == "All" ? "" : customerFilterTypedText}
										value={customerFilter ? customerOptions.find(option => option.value === customerFilter) || null : { name: '', value: '' }}
										onChange={(event, newValue) => { setCustomerFilter(newValue?.value ?? ''); setCustomerFilterTypedText(newValue?.name ?? ''); }}
										onInputChange={(event, newInputValue, reason) => { if (reason === 'input') { setCustomerFilterTypedText(newInputValue); } }}
										onKeyDown={(event) => { if ((event.key === 'Enter' && !customerFilterTypedText) || event.key === 'Escape') { setCustomerFilter(''); setCustomerFilterTypedText(''); } }}
										getOptionLabel={(item) => item?.value == '' ? '' : item?.name ?? ''}
										isOptionEqualToValue={(item, value) => item?.value === value.value}
									/>
								</FormControl>
								<FormControl size="small" className={`vendor-field ${isLoadingShipments && "disabled-appearance"}`}>
									<Autocomplete
										autoHighlight
										blurOnSelect
										disablePortal
										disableClearable
										options={[{ name: 'All', value: '' }, ...vendorOptions.filter(c => !searchResults?.records?.length || (includedVendorIds?.includes(+c.value) ?? false))]}
										renderInput={(params) => <TextField {...params} label="Vendor" />}
										renderOption={(props, item: ISelectOption, { index }) => (<li {...props} className={!item.isActive && item.value ? "filter-item-inactive" : ""} value={item.value} key={index}>{item.name}{!item.isActive && item.value && " (Inactive)"}</li>)}
										inputValue={vendorFilterTypedText == "All" ? "" : vendorFilterTypedText}
										value={vendorFilter ? vendorOptions.find(option => option.value === vendorFilter) || null : { name: 'All', value: '' }}
										onChange={(event, newValue) => { setVendorFilter(newValue?.value ?? ''); setVendorFilterTypedText(newValue?.name ?? ''); }}
										onInputChange={(event, newInputValue, reason) => { if (reason === 'input') { setVendorFilterTypedText(newInputValue); } }}
										onKeyDown={(event) => { if ((event.key === 'Enter' && !vendorFilterTypedText) || event.key === 'Escape') { setVendorFilter(''); setVendorFilterTypedText(''); } }}
										getOptionLabel={(item) => item?.value == '' ? '' : item?.name ?? ''}
										isOptionEqualToValue={(item, value) => item?.value === value.value}
									/>
								</FormControl>
								<FormControl size="small" className={`status-field ${isLoadingShipments && "disabled-appearance"}`}>
									<InputLabel>Status</InputLabel>
									<Select
										value={statusFilter}
										label="Status"
										onChange={(event: SelectChangeEvent) => { setStatusFilter(event.target.value) }}
										onClose={blurActiveControl}
									>
										<MenuItem value="" key="-1">All</MenuItem>
										{statusOptions.map((item, index) => (
											<MenuItem value={item} key={index}>{item}</MenuItem>
										))}
									</Select>
								</FormControl>
								<div className="search-button">
									<Button variant="outlined" fullWidth={true} onClick={() => { loadShipmentData(true); }}>Search</Button>
								</div>
							</div>
						</section>
						<section className="results">
							{successMessage &&
								<Alert className="alert-success"
									severity="success"
									action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
									{successMessage}
								</Alert>
							}
							{errorMessage &&
								<Alert className="alert-error"
									severity="error">
									{errorMessage}
								</Alert>
							}
							{criteriaErrorMessage &&
								<Alert className="alert-error"
									severity="error">
									{criteriaErrorMessage}
								</Alert>
							}
							{restoringErrorMessage &&
								<Alert className="alert-error"
									severity="error">
									{restoringErrorMessage}
								</Alert>
							}
							{!errorMessage &&
								<DeletedShipmentTable
									data={searchResults}
									bol={bolFilter}
									customer={customerFilter}
									vendor={vendorFilter}
									status={statusFilter}
									sortField={sortField}
									sortAscending={sortAscending}
									isAwaitingResultSet={isLoadingShipments}
									userHasSearched={userHasSearched}
									update={criteriaUpdated}
									onRestoreShipmentRequest={handleRestoreShipmentRequest}
									></DeletedShipmentTable>
							}
						</section>
						{/* <Json data={{ startDate, endDate }}></Json> */}
					</LocalizationProvider>
				</PermissionGate>
				<WorkingModal params={workingModalParams?.params}></WorkingModal>
			</Container>
		</div>
	);
};

export default DeletedShipmentList;